import { useMemo, useState, type PointerEvent } from 'react'
import { Link } from 'react-router-dom'
import { Correct } from '../../components/Correct'
import { PillarChip } from '../../components/PillarChip'
import { formatCompact, formatDate, formatMoney } from '../../lib/format'
import { scopeWords } from '../../lib/orgs'
import { ageWords, linePoints, nearestIndex, pct, routeForPursuit, segmentWidths, shortDate, weekTicks, xAt, yAt, yTicks, type ChartBox } from '../../lib/pipeline'
import type { Bucket, Focus, FunnelStep, MissedPursuit, PersonRef, PipelineStats, PipelineTab, PursuitRef, QuadrantCell, StatCallout, WeekPoint } from '../../types/pipeline'
import './pipeline.css'

// The analytics band at the top of Leads and Opportunities. Every number here arrived computed from the API
// (PipelineMath) and every number is a button: it focuses the list below on the pursuits behind it, or opens
// the item. Stage history is not stored, so the over-time chart is an approximation and says so.

export interface BandProps {
  stats: PipelineStats
  tab: PipelineTab
  focus: Focus | null
  onFocus: (focus: Focus | null) => void
}

const money = (v: number) => (v >= 1_000_000 ? '$' + formatCompact(v) : formatMoney(v))

export function PipelineBand({ stats, tab, focus, onFocus }: BandProps) {
  const g = stats.glance
  const pick = (label: string, ids: string[], refs: PursuitRef[] = []) => onFocus(focus?.label === label ? null : { label, ids, refs })
  const on = (label: string) => (focus?.label === label ? 'on' : '')
  const missedIds = stats.missed.map((m) => m.pursuit.engagementId)
  const missedRefs = stats.missed.map((m) => m.pursuit)
  const owedUs = stats.nextSteps.owedByUs
  const owedThem = stats.nextSteps.owedByThem
  const wl = stats.winLoss

  return (
    <>
      {stats.callouts.length > 0 && (
        <ul className="lead-callouts band-callouts" aria-label="What to notice">
          {stats.callouts.map((c) => <Callout key={c.kind} c={c} on={on(c.title)} onPick={() => pick(c.title, c.ids)} />)}
        </ul>
      )}

      <section className="glance pipeline-band" aria-label={`${tab === 'leads' ? 'Leads' : 'Opportunities'} at a glance`}>
        <div className="band-stats">
          <p className="kicker">{scopeWords(stats.scope) ?? 'The pipeline'} · {tab === 'leads' ? 'leads' : 'opportunities'} at a glance <PillarChip pillar={stats.pillar} /></p>
          <div className="glance-stats">
            {tab === 'leads' ? (
              <>
                <Stat n={g.live} label="live leads" on={on('Live leads')} onClick={() => pick('Live leads', g.liveIds)} />
                <Stat n={g.aging} label="aging" warn={g.aging > 0} on={on('Aging leads')} onClick={() => pick('Aging leads', g.agingIds)} />
                <Stat n={g.missed} label="missed" sub={g.missedRecoverable > 0 ? `${g.missedRecoverable} recoverable` : undefined} warn={g.missedRecoverable > 0} on={on('Missed opportunities')} onClick={() => pick('Missed opportunities', missedIds, missedRefs)} />
                <Stat n={g.newLast4Weeks} label="new, 4 weeks" sub={`${g.newPrior4Weeks} the 4 before`} tone={g.newLast4Weeks >= g.newPrior4Weeks ? 'up' : 'down'} on={on('New in the last four weeks')} onClick={() => pick('New in the last four weeks', newIds(stats, 4))} />
                <Stat n={g.reactivated} label="came back" on={on('Came back after a quiet spell')} onClick={() => pick('Came back after a quiet spell', g.reactivatedIds)} />
                <Stat n={g.owedByUs} label="you owe" sub={g.overdue > 0 ? `${g.overdue} overdue` : undefined} warn={g.overdue > 0} on={on('Next steps you owe')} onClick={() => pick('Next steps you owe', owedUs.map((l) => l.pursuit.engagementId))} />
                <Stat n={g.owedByThem} label="they owe" on={on('Next steps they owe')} onClick={() => pick('Next steps they owe', owedThem.map((l) => l.pursuit.engagementId))} />
                <Stat n={g.recoverFirst} label="recover first" sub="warm ties gone quiet" warn={g.recoverFirst > 0} onClick={() => document.getElementById('recovery')?.scrollIntoView({ behavior: 'smooth', block: 'center' })} />
              </>
            ) : (
              <>
                <Stat n={g.live} label="open" sub={g.aging > 0 ? `${g.aging} aging` : undefined} on={on('Open opportunities')} onClick={() => pick('Open opportunities', g.liveIds)} />
                <Stat text={g.namedPipeline > 0 ? money(g.namedPipeline) : '—'} label="named" on={on('Named pipeline')} onClick={() => pick('Named pipeline', stats.stages.filter((s) => s.stage !== 'won').flatMap((s) => s.ids))} />
                <Stat text={g.weightedPipeline > 0 ? money(g.weightedPipeline) : '—'} label="weighted" sub="pack weights" onClick={() => document.getElementById('stages')?.scrollIntoView({ behavior: 'smooth', block: 'center' })} />
                <Stat text={pct(g.winRate)} label="win rate" sub={`${stats.velocity.won} won · ${stats.velocity.lost} lost, 26 wk`} on={on('Won and lost in the window')} onClick={() => pick('Won and lost in the window', [...wl.wonIds, ...wl.lostIds])} />
                <Stat text={g.cycleDays != null ? `${g.cycleDays}d` : '—'} label="median cycle" sub="first evidence to decision" on={on('Won')} onClick={() => pick('Won', wl.wonIds)} />
                <Stat n={g.proposalsOut} label="proposals out" sub={g.proposalsStale > 0 ? `${g.proposalsStale} past 21 days` : undefined} warn={g.proposalsStale > 0} on={on('Proposals out')} onClick={() => pick('Proposals out', stats.proposalsOut.flatMap((b) => b.ids))} />
                <Stat n={g.owedByUs} label="you owe" sub={g.overdue > 0 ? `${g.overdue} overdue` : undefined} warn={g.overdue > 0} on={on('Next steps you owe')} onClick={() => pick('Next steps you owe', owedUs.map((l) => l.pursuit.engagementId))} />
                <Stat n={g.missed} label="missed" sub={g.missedRecoverable > 0 ? `${g.missedRecoverable} recoverable` : undefined} warn={g.missedRecoverable > 0} on={on('Missed opportunities')} onClick={() => pick('Missed opportunities', missedIds, missedRefs)} />
              </>
            )}
          </div>
        </div>

        <div className="band-chart">
          <OverTime points={stats.overTime} tab={tab} />
          <AgingBar buckets={stats.aging} title={tab === 'leads' ? 'Leads by days since last activity' : 'Open opportunities by days since last activity'} focus={focus} onPick={pick} />
        </div>

        <div className="band-funnel">
          <Funnel steps={stats.funnel} tab={tab} focus={focus} onPick={pick} />
          <Velocity stats={stats} tab={tab} />
        </div>

        <details className="band-foot">
          <summary>How these are counted</summary>
          <ul>{stats.approximations.map((a) => <li key={a}>{a}</li>)}</ul>
          <p className="kicker">Lead lifecycle, days from last activity</p>
          <table className="lifespans">
            <thead><tr><th>Kind</th><th>Aging after</th><th>Missed after</th><th>Basis</th></tr></thead>
            <tbody>{stats.lifespans.map((l) => <tr key={l.kind}><td>{l.label}</td><td>{l.agingAfterDays}d</td><td>{l.missedAfterDays}d</td><td>{l.basis}</td></tr>)}</tbody>
          </table>
        </details>
      </section>

      <div className={`band-cards ${tab}`}>
        {tab === 'leads' ? (
          <>
            <Recovery cells={stats.recovery} />
            <NextStepsCard stats={stats} focus={focus} onPick={pick} />
          </>
        ) : (
          <>
            <StagesCard stats={stats} focus={focus} onPick={pick} />
            <ProposalsCard stats={stats} focus={focus} onPick={pick} />
            <WinLossCard stats={stats} focus={focus} onPick={pick} />
            <NextStepsCard stats={stats} focus={focus} onPick={pick} />
            {stats.tactics.length > 0 && <TacticsCard stats={stats} />}
          </>
        )}
      </div>
    </>
  )
}

/** Ids of pursuits whose first evidence fell in the last n weeks: the over-time points carry counts only, so the
 * glance tile focuses on the ids the API attached to the "new" callout when it exists, else on nothing. */
function newIds(stats: PipelineStats, _weeks: number): string[] {
  const c = stats.callouts.find((x) => x.kind === 'intake-down' || x.kind === 'intake-up')
  return c?.ids ?? []
}

/* ── pieces ── */

function Stat({ n, text, label, sub, warn, tone, on, onClick }: { n?: number; text?: string; label: string; sub?: string; warn?: boolean; tone?: 'up' | 'down'; on?: string; onClick: () => void }) {
  return (
    <button type="button" className={`glance-stat as-button ${warn ? 'warn' : ''} ${tone ?? ''} ${on ?? ''}`} onClick={onClick}>
      <strong>{text ?? n}</strong>
      <span>{label}</span>
      {sub && <small>{sub}</small>}
    </button>
  )
}

function Callout({ c, on, onPick }: { c: StatCallout; on: string; onPick: () => void }) {
  return (
    <li className={`callout ${c.severity} ${on}`}>
      <strong>{c.title}</strong>
      <span>{c.detail}</span>
      <div className="callout-actions">
        {c.ids.length > 0 && <button type="button" className="button small" onClick={onPick}>{on ? 'Show all' : `Show the ${c.count === 1 ? 'one' : c.count}`}</button>}
        {c.tacticName && <Link className="play-chip" to="/playbook" title="Open the playbook">Play: {c.tacticName}</Link>}
      </div>
    </li>
  )
}

const BOX: ChartBox = { width: 520, height: 150, left: 30, right: 14, top: 12, bottom: 24 }

interface Series { key: keyof WeekPoint; label: string; cls: string }
const LEAD_SERIES: Series[] = [
  { key: 'new', label: 'new', cls: 's1' },
  { key: 'reactivated', label: 'came back', cls: 's2' },
  { key: 'wentQuiet', label: 'went quiet', cls: 's3' },
]
const OPP_SERIES: Series[] = [
  { key: 'newOpportunities', label: 'new opportunities', cls: 's1' },
  { key: 'proposalsOut', label: 'proposals out', cls: 's2' },
  { key: 'won', label: 'won', cls: 's3' },
  { key: 'lost', label: 'lost', cls: 's4' },
]

function OverTime({ points, tab }: { points: WeekPoint[]; tab: PipelineTab }) {
  const series = tab === 'leads' ? LEAD_SERIES : OPP_SERIES
  const [hover, setHover] = useState<number | null>(null)
  const max = useMemo(() => Math.max(1, ...points.flatMap((p) => series.map((s) => p[s.key] as number))), [points, series])
  const ticks = yTicks(max)
  const top = ticks[ticks.length - 1]
  const n = points.length
  const move = (e: PointerEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect()
    setHover(nearestIndex(((e.clientX - r.left) / r.width) * BOX.width, n, BOX))
  }
  const h = hover != null ? points[hover] : null
  const empty = points.every((p) => series.every((s) => (p[s.key] as number) === 0))
  return (
    <div className="over-time">
      <div className="chart-head">
        <p className="kicker">{tab === 'leads' ? 'Leads over time' : 'Opportunities over time'} · {n} weeks <em>approximate: from dated evidence, not a stage history</em></p>
        <ul className="legend">{series.map((s) => <li key={s.key}><i className={`key ${s.cls}`} />{s.label}</li>)}</ul>
      </div>
      <div className="chart-wrap">
        <svg className="week-chart" viewBox={`0 0 ${BOX.width} ${BOX.height}`} role="img" aria-label={`${tab} per week`} onPointerMove={move} onPointerLeave={() => setHover(null)}>
          {ticks.map((t) => <line key={t} className="grid" x1={BOX.left} x2={BOX.width - BOX.right} y1={yAt(t, top, BOX)} y2={yAt(t, top, BOX)} />)}
          {ticks.map((t) => <text key={`y${t}`} className="tick" x={BOX.left - 6} y={yAt(t, top, BOX) + 3} textAnchor="end">{t}</text>)}
          {weekTicks(points).map((i) => <text key={`x${i}`} className="tick" x={xAt(i, n, BOX)} y={BOX.height - 6} textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}>{shortDate(points[i].weekStart)}</text>)}
          {!empty && series.map((s) => <polyline key={s.key} className={`line ${s.cls}`} points={linePoints(points.map((p) => p[s.key] as number), top, BOX)} />)}
          {!empty && series.map((s) => {
            const v = points[n - 1][s.key] as number
            return <circle key={`e${s.key}`} className={`end ${s.cls}`} cx={xAt(n - 1, n, BOX)} cy={yAt(v, top, BOX)} r={4} />
          })}
          {hover != null && <line className="crosshair" x1={xAt(hover, n, BOX)} x2={xAt(hover, n, BOX)} y1={BOX.top} y2={BOX.height - BOX.bottom} />}
          {hover != null && series.map((s) => <circle key={`h${s.key}`} className={`end ${s.cls}`} cx={xAt(hover, n, BOX)} cy={yAt(points[hover][s.key] as number, top, BOX)} r={4} />)}
          {empty && <text className="tick" x={BOX.width / 2} y={BOX.height / 2} textAnchor="middle">Nothing dated in the last {n} weeks.</text>}
        </svg>
        {h && (
          <div className="chart-tip" style={{ left: `${(xAt(hover!, n, BOX) / BOX.width) * 100}%` }}>
            <p className="tip-when">week of {shortDate(h.weekStart)}</p>
            {series.map((s) => <p key={s.key}><i className={`key ${s.cls}`} /><strong>{h[s.key] as number}</strong> {s.label}</p>)}
          </div>
        )}
      </div>
    </div>
  )
}

function AgingBar({ buckets, title, focus, onPick }: { buckets: Bucket[]; title: string; focus: Focus | null; onPick: (label: string, ids: string[], refs?: PursuitRef[]) => void }) {
  const widths = segmentWidths(buckets)
  const total = buckets.reduce((n, b) => n + b.count, 0)
  return (
    <div className="aging">
      <p className="kicker">{title} <em>{total} in all</em></p>
      {total === 0 ? <p className="muted small">Nothing to age yet.</p> : (
        <>
          <div className="seg-bar" role="group" aria-label={title}>
            {buckets.map((b, i) => b.count > 0 && (
              <button key={b.key} type="button" className={`seg age-${i} ${b.beyondLifespan ? 'beyond' : ''} ${focus?.label === b.label ? 'on' : ''}`} style={{ width: `${widths[i]}%` }}
                title={`${b.label}: ${b.count}${b.amount > 0 ? ` · ${formatMoney(b.amount)} named` : ''}`} onClick={() => onPick(b.label, b.ids, b.top)}>
                {widths[i] > 9 && <span>{b.count}</span>}
              </button>
            ))}
          </div>
          <ul className="seg-legend">
            {buckets.map((b, i) => <li key={b.key} className={b.count === 0 ? 'empty' : ''}><i className={`key age-${i} ${b.beyondLifespan ? 'beyond' : ''}`} /><button type="button" className="link-button" onClick={() => onPick(b.label, b.ids, b.top)} disabled={b.count === 0}>{b.label}</button> <b>{b.count}</b></li>)}
          </ul>
        </>
      )}
    </div>
  )
}

function Funnel({ steps, tab, focus, onPick }: { steps: FunnelStep[]; tab: PipelineTab; focus: Focus | null; onPick: (label: string, ids: string[], refs?: PursuitRef[]) => void }) {
  const max = Math.max(1, ...steps.map((s) => s.count))
  return (
    <div className="funnel">
      <p className="kicker">Conversion <em>every pursuit on file{tab === 'leads' ? '' : ', both tabs'}</em></p>
      <ol>
        {steps.map((s, i) => (
          <li key={s.key} className={focus?.label === s.label ? 'on' : ''}>
            {i > 0 && (
              <p className="between">
                <span>{pct(s.rateFromPrior)} of the step before</span>
                {s.medianDaysFromPrior != null && s.medianDaysFromPrior > 0 && <span title={s.daysBasis ?? ''}>{s.medianDaysFromPrior}d median</span>}
              </p>
            )}
            <button type="button" className="funnel-step" onClick={() => onPick(s.label, s.ids, s.top)} title={s.amount > 0 ? `${formatMoney(s.amount)} named` : ''}>
              <span className="bar"><span style={{ width: `${(s.count / max) * 100}%` }} /></span>
              <span className="label">{s.label}</span>
              <strong>{s.count}</strong>
            </button>
          </li>
        ))}
      </ol>
    </div>
  )
}

function Velocity({ stats, tab }: { stats: PipelineStats; tab: PipelineTab }) {
  const v = stats.velocity
  if (tab === 'leads') {
    return (
      <p className="velocity">
        <strong>{v.intakePerWeek}</strong> new a week over {stats.overTime.length} weeks
        {stats.glance.leadToOpportunityRate != null && <>; <strong>{pct(stats.glance.leadToOpportunityRate)}</strong> reach a scoped need</>}.
      </p>
    )
  }
  return (
    <p className="velocity" title="open × win rate × average won ÷ cycle weeks">
      {v.perWeek != null ? <><strong>{money(v.perWeek)}</strong> a week of expected wins: {v.open} open × {pct(v.winRate)} × {money(v.averageWon!)} ÷ {v.cycleDays} days.</> : <>Velocity needs a win and a loss in the window with an amount named; {v.open} open now.</>}
    </p>
  )
}

function Recovery({ cells }: { cells: QuadrantCell[] }) {
  const [open, setOpen] = useState<string | null>(null)
  return (
    <div className="card recovery-card" id="recovery">
      <div className="card-head">
        <div>
          <h2>Recovery quadrant</h2>
          <p className="muted">Prior strength of the tie against how long it has been quiet. Warm is 30% or more; quiet is 90 days or more. Click a name for the account.</p>
        </div>
      </div>
      <div className="quadrant">
        <span className="axis y">strength of the tie</span>
        <span className="axis x">time quiet</span>
        {cells.map((c) => (
          <div key={c.key} className={`cell ${c.key}`}>
            <div className="cell-head">
              <h3>{c.label}</h3>
              <button type="button" className="count" onClick={() => setOpen(open === c.key ? null : c.key)} disabled={c.count === 0} title={c.count > c.top.length ? `${c.top.length} of ${c.count} named` : ''}>{c.count}</button>
            </div>
            <p className="advice">{c.advice}</p>
            <ul className="names">
              {c.top.slice(0, open === c.key ? c.top.length : 4).map((p) => <PersonLine key={p.personId} p={p} />)}
              {c.count > c.top.length && open === c.key && <li className="muted small">and {c.count - c.top.length} more on Contacts</li>}
              {c.count > 4 && open !== c.key && <li><button type="button" className="link-button small" onClick={() => setOpen(c.key)}>+{c.count - 4} more</button></li>}
            </ul>
            {c.tacticName && <Link className="play-chip" to="/playbook">Play: {c.tacticName}</Link>}
          </div>
        ))}
      </div>
    </div>
  )
}

function PersonLine({ p }: { p: PersonRef }) {
  return (
    <li>
      {p.companyId ? <Link to={`/accounts/${encodeURIComponent(p.companyId)}`}>{p.name}</Link> : <span>{p.name}</span>}
      <span className="muted small">{p.companyName} · {Math.round(p.strength * 100)}% · {p.dormancyDays >= 100000 ? 'never touched' : `${ageWords(p.dormancyDays)} quiet`}</span>
      {p.engagementId && p.subject && <Link className="small" to={routeForPursuit({ engagementId: p.engagementId, tab: p.stage === 'lead' || p.stage === 'dormant' ? 'leads' : 'opportunities' })}>{p.subject}</Link>}
    </li>
  )
}

function NextStepsCard({ stats, focus, onPick }: { stats: PipelineStats; focus: Focus | null; onPick: (label: string, ids: string[]) => void }) {
  const ns = stats.nextSteps
  const Row = ({ l }: { l: (typeof ns.owedByUs)[number] }) => (
    <li className={l.overdue ? 'overdue' : ''}>
      <Link to={routeForPursuit(l.pursuit)}>{l.pursuit.companyName}</Link>
      <span className="muted small">{l.by ? (l.overdue ? `${l.daysOverdue} day${l.daysOverdue === 1 ? '' : 's'} overdue` : `by ${formatDate(l.by)}`) : 'no date'}</span>
      <span className="step">{l.nextStep}</span>
    </li>
  )
  return (
    <div className="card next-steps-card">
      <div className="card-head">
        <div>
          <h2>Next steps</h2>
          <p className="muted">Only promises that are still live: an open pursuit, a thread that moved inside 26 weeks, a date not more than a quarter past. {ns.lapsed > 0 && <button type="button" className={`link-button ${focus?.label === 'Lapsed promises' ? 'on' : ''}`} onClick={() => onPick('Lapsed promises', ns.lapsedIds)}>{ns.lapsed} older {ns.lapsed === 1 ? 'promise has' : 'promises have'} lapsed.</button>}</p>
        </div>
      </div>
      <div className="two-up owed-cols">
        <div>
          <h3>You owe <b>{ns.owedByUs.length}</b></h3>
          {ns.owedByUs.length === 0 ? <p className="muted small">Nothing is waiting on you.</p> : <ul className="owed-list">{ns.owedByUs.slice(0, 6).map((l) => <Row key={l.pursuit.engagementId} l={l} />)}</ul>}
          {ns.owedByUs.length > 6 && <button type="button" className="link-button small" onClick={() => onPick('Next steps you owe', ns.owedByUs.map((l) => l.pursuit.engagementId))}>All {ns.owedByUs.length} in the list</button>}
        </div>
        <div>
          <h3>They owe <b>{ns.owedByThem.length}</b></h3>
          {ns.owedByThem.length === 0 ? <p className="muted small">Nothing is waiting on them.</p> : <ul className="owed-list">{ns.owedByThem.slice(0, 6).map((l) => <Row key={l.pursuit.engagementId} l={l} />)}</ul>}
          {ns.owedByThem.length > 6 && <button type="button" className="link-button small" onClick={() => onPick('Next steps they owe', ns.owedByThem.map((l) => l.pursuit.engagementId))}>All {ns.owedByThem.length} in the list</button>}
        </div>
      </div>
    </div>
  )
}

function StagesCard({ stats, focus, onPick }: { stats: PipelineStats; focus: Focus | null; onPick: (label: string, ids: string[], refs?: PursuitRef[]) => void }) {
  const named = stats.glance.namedPipeline
  const weighted = stats.glance.weightedPipeline
  return (
    <div className="card stages-card" id="stages">
      <div className="card-head">
        <div>
          <h2>Stage funnel and weighted pipeline</h2>
          <p className="muted">Amounts as written in the mail, and what the pack's stage weights make of them. The weights are starting values; the firm's own funnel retunes them.</p>
        </div>
      </div>
      <table className="grid stages-grid">
        <thead><tr><th>Stage</th><th className="num">Pursuits</th><th className="num">Named</th><th className="num">Weight</th><th className="num">Weighted</th><th className="num">Median age</th><th className="num">Oldest</th></tr></thead>
        <tbody>
          {stats.stages.map((s) => (
            <tr key={s.stage} className={`${focus?.label === s.label ? 'on' : ''} ${s.count === 0 ? 'empty' : ''}`} onClick={() => s.count > 0 && onPick(s.label, s.ids, s.top)}>
              <td><span className={`tag stage ${s.stage}`}>{s.label}</span></td>
              <td className="num"><strong>{s.count}</strong></td>
              <td className="num">{s.amount > 0 ? formatMoney(s.amount) : '—'}</td>
              <td className="num muted">{Math.round(s.weight * 100)}%</td>
              <td className="num">{s.weighted > 0 ? formatMoney(s.weighted) : '—'}</td>
              <td className="num">{s.medianDays != null ? `${s.medianDays}d` : '—'}</td>
              <td className="num">{s.oldestDays != null ? ageWords(s.oldestDays) : '—'}</td>
            </tr>
          ))}
        </tbody>
        <tfoot><tr><td>Open pipeline</td><td /><td className="num"><strong>{named > 0 ? formatMoney(named) : '—'}</strong></td><td /><td className="num"><strong>{weighted > 0 ? formatMoney(weighted) : '—'}</strong></td><td colSpan={2} /></tr></tfoot>
      </table>
    </div>
  )
}

function ProposalsCard({ stats, focus, onPick }: { stats: PipelineStats; focus: Focus | null; onPick: (label: string, ids: string[], refs?: PursuitRef[]) => void }) {
  const buckets = stats.proposalsOut
  const total = buckets.reduce((n, b) => n + b.count, 0)
  const widths = segmentWidths(buckets)
  return (
    <div className="card proposals-card">
      <div className="card-head">
        <div>
          <h2>Proposals out, waiting</h2>
          <p className="muted">Days since the proposal or SOW went out, with no decision on record. Three weeks is a nudge; a quarter is a no-decision loss in the making.</p>
        </div>
      </div>
      {total === 0 ? <p className="muted small">No proposal is out waiting on an answer.</p> : (
        <>
          <div className="seg-bar light" role="group" aria-label="Proposals out by age">
            {buckets.map((b, i) => b.count > 0 && (
              <button key={b.key} type="button" className={`seg prop-${i} ${b.beyondLifespan ? 'beyond' : ''} ${focus?.label === `Proposals out ${b.label}` ? 'on' : ''}`} style={{ width: `${widths[i]}%` }} title={`${b.label}: ${b.count}`} onClick={() => onPick(`Proposals out ${b.label}`, b.ids, b.top)}>
                {widths[i] > 9 && <span>{b.count}</span>}
              </button>
            ))}
          </div>
          <ul className="seg-legend light">
            {buckets.map((b, i) => <li key={b.key} className={b.count === 0 ? 'empty' : ''}><i className={`key prop-${i} ${b.beyondLifespan ? 'beyond' : ''}`} /><button type="button" className="link-button" disabled={b.count === 0} onClick={() => onPick(`Proposals out ${b.label}`, b.ids, b.top)}>{b.label}</button> <b>{b.count}</b>{b.amount > 0 && <span className="muted small"> · {formatMoney(b.amount)}</span>}</li>)}
          </ul>
        </>
      )}
    </div>
  )
}

function WinLossCard({ stats, focus, onPick }: { stats: PipelineStats; focus: Focus | null; onPick: (label: string, ids: string[]) => void }) {
  const w = stats.winLoss
  return (
    <div className="card winloss-card">
      <div className="card-head">
        <div>
          <h2>Win / loss</h2>
          <p className="muted">Every decision on file, and the reasons the decision messages carried. Most losses are to no decision, not a rival.</p>
        </div>
      </div>
      <div className="deal-totals wl-totals">
        <button type="button" className={`stat as-button ${focus?.label === 'Won' ? 'on' : ''}`} onClick={() => onPick('Won', w.wonIds)}><strong>{w.won}</strong><span className="muted">won{w.wonAmount > 0 ? ` · ${money(w.wonAmount)}` : ''}</span></button>
        <button type="button" className={`stat as-button ${focus?.label === 'Lost' ? 'on' : ''}`} onClick={() => onPick('Lost', w.lostIds)}><strong>{w.lost}</strong><span className="muted">lost{w.lostAmount > 0 ? ` · ${money(w.lostAmount)}` : ''}</span></button>
        <button type="button" className={`stat as-button ${w.noDecision > 0 ? 'warn' : ''} ${focus?.label === 'No decision' ? 'on' : ''}`} onClick={() => onPick('No decision', w.noDecisionIds)}><strong>{w.noDecision}</strong><span className="muted">no decision, 90+ days</span></button>
        <div className="stat"><strong>{pct(w.winRate)}</strong><span className="muted">win rate, all time</span></div>
      </div>
      {w.reasons.length > 0 && (
        <>
          <h3>Reasons on file</h3>
          <ul className="plain reasons">
            {w.reasons.map((r) => (
              <li key={`${r.engagementId}-${r.on}`}>
                <span className={`tag stage ${r.outcome}`}>{r.outcome}</span> <Link to={routeForPursuit({ engagementId: r.engagementId, tab: r.tab })}>{r.companyName}</Link>
                <span className="muted small"> · {formatDate(r.on)}{r.amount ? ` · ${formatMoney(r.amount)}` : ''}</span>
                <span className="reason">{r.headline}{r.detail ? ` — ${r.detail}` : ''}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

function TacticsCard({ stats }: { stats: PipelineStats }) {
  return (
    <div className="card tactics-card">
      <div className="card-head">
        <div>
          <h2>Plays run</h2>
          <p className="muted">How the methodology's tactics landed on your pursuits. Conversion is booked, advanced or won over applied.</p>
        </div>
        <Link className="button small" to="/playbook">Playbook</Link>
      </div>
      <table className="grid tactics-grid">
        <thead><tr><th>Tactic</th><th className="num">Applied</th><th className="num">Booked</th><th className="num">Advanced</th><th className="num">Won</th><th className="num">No response</th><th className="num">Conversion</th></tr></thead>
        <tbody>{stats.tactics.map((t) => <tr key={t.tacticId}><td><strong>{t.name}</strong><span className="muted small">{t.category}</span></td><td className="num">{t.applied}</td><td className="num">{t.booked}</td><td className="num">{t.advanced}</td><td className="num">{t.won}</td><td className="num">{t.noResponse}</td><td className="num">{pct(t.conversion)}</td></tr>)}</tbody>
      </table>
    </div>
  )
}

/** The missed opportunities section below the list: what died, where, and whether a warm tie makes it recoverable. */
export function MissedCard({ missed, tab }: { missed: MissedPursuit[]; tab: PipelineTab }) {
  const [all, setAll] = useState(false)
  if (missed.length === 0) return null
  const rows = all ? missed : missed.slice(0, 12)
  const recoverable = missed.filter((m) => m.recoverable).length
  return (
    <div className="card missed-card" id="missed">
      <div className="card-head">
        <div>
          <h2>Missed opportunities <span className="count">{missed.length}</span></h2>
          <p className="muted">
            Pursuits past the lifespan of their kind since the last thing said, kept out of the {tab === 'leads' ? 'leads' : 'opportunities'} above so the list reads as a pipeline.
            {/* whose warm tie it is depends on the org selected, and the row already names the person: say what is true of all of them */}
            {recoverable > 0 ? ` ${recoverable} ${recoverable === 1 ? 'sits' : 'sit'} where a warm tie is still on file: those are the first to recover.` : ''}
          </p>
        </div>
      </div>
      <table className="grid missed-grid">
        <thead><tr><th>Company</th><th>Died in</th><th>Quiet for</th><th>The last thing said</th><th>Warmest tie</th><th>Recover</th><th className="correct-col"><span className="visually-hidden">Not right?</span></th></tr></thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.pursuit.engagementId} className={m.recoverable ? 'recoverable' : ''}>
              <td><Link to={routeForPursuit(m.pursuit)}><strong>{m.pursuit.companyName}</strong></Link><span className="muted small">{m.pursuit.subject}</span></td>
              <td><span className={`tag stage ${m.pursuit.stage}`}>{m.diedIn}</span></td>
              <td><strong>{ageWords(m.pursuit.days)}</strong><span className="muted small">lifespan {m.lifespanDays}d{m.pursuit.lastActivityOn ? ` · last ${formatDate(m.pursuit.lastActivityOn)}` : ''}</span></td>
              <td className="said">{m.lastSaid}</td>
              <td>{m.strongestPersonName ? <>{m.strongestPersonId && m.pursuit.companyId ? <Link to={`/accounts/${encodeURIComponent(m.pursuit.companyId)}`}>{m.strongestPersonName}</Link> : m.strongestPersonName}<span className="muted small">{Math.round(m.strongestTie * 100)}%</span></> : <span className="muted">nobody on file</span>}</td>
              <td>{m.recoverable ? <Link className="play-chip" to={routeForPursuit(m.pursuit)} title={m.tacticName ?? ''}>Recoverable{m.tacticName ? ` · ${m.tacticName}` : ''}</Link> : <span className="muted small">let go</span>}</td>
              <td className="correct-col">
                <Correct kind="lifecycle" entityId={m.pursuit.engagementId} entityLabel={`${m.pursuit.companyName} · ${m.pursuit.subject}`} currentValue="missed" compact />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {missed.length > 12 && <button type="button" className="link-button small" onClick={() => setAll(!all)}>{all ? 'Show the first 12' : `Show all ${missed.length}`}</button>}
    </div>
  )
}
